/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { styles } from '../posShared.styles';
import { useEmployeesStore, withSelfOwner } from '../../../team/store/useEmployeesStore';
import type { EmployeeRecord } from '../../../team/store/useEmployeesStore';
import { usePosCheckoutContextOptional } from '../contexts/PosCheckoutContext';
import { PosCartLineAttributionPicker } from '../cart/PosCartLineAttributionPicker';
import { useHasBusinessCapability } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { useSessionStore } from '../../../shell/session/store/useSessionStore';
import { simulateCommissionsBatch } from '../../../commissions/public-api';
import { showError, showInfo } from '../../../shared/ui/appToast';
import { formatPosMoney } from '../../application/posMoneyFormat';

type Props = {
  layoutWide: boolean;
};

export function PosCheckoutAttributionPanel({ layoutWide }: Props) {
  const ctx = usePosCheckoutContextOptional();
  const hasCommissions = useHasBusinessCapability('commissions');
  const employees = useEmployeesStore((s) => s.employees);
  const selfOwner = useEmployeesStore((s) => s.selfOwner);
  const loadEmployees = useEmployeesStore((s) => s.loadEmployees);
  const [pickerLineId, setPickerLineId] = useState<string | null>(null);
  const [pickerItemName, setPickerItemName] = useState('');
  const [pickerPerformedBy, setPickerPerformedBy] = useState<string | null>(null);
  const [pickerSoldBy, setPickerSoldBy] = useState<string | null>(null);
  // Colapsado por default: la atribución es opcional y no debe parecer error.
  const [expanded, setExpanded] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [simResult, setSimResult] = useState<{ total: number; count: number } | null>(null);
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const simCallIdRef = useRef(0);

  useEffect(() => {
    if (hasCommissions) void loadEmployees();
  }, [hasCommissions, loadEmployees]);

  const cart = ctx?.cart ?? [];
  const onSetCartLineAttribution = ctx?.onSetCartLineAttribution;

  const activeEmployees = useMemo(
    () => withSelfOwner(employees, selfOwner).filter((e) => e.isActive !== false),
    [employees, selfOwner],
  );

  const employeeName = (userId: string | null): string => {
    if (!userId) return 'Cajero (default)';
    const emp = activeEmployees.find((e) => e.authUserId === userId);
    return emp?.name ?? 'Desconocido';
  };

  const employeeInitials = (userId: string | null): string => {
    if (!userId) return '?';
    const emp = activeEmployees.find((e) => e.authUserId === userId);
    if (!emp?.name) return '?';
    const parts = emp.name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  };

  // Solo mostrar si el feature de comisiones está activo y hay items en el carrito
  if (!hasCommissions || cart.length === 0 || !onSetCartLineAttribution) return null;

  // Filtrar líneas que son servicios (necesitan performed_by) o productos (sold_by opcional)
  const serviceLines = cart.filter((item) => item.catalogItemType === 'service');
  const productLines = cart.filter(
    (item) => item.catalogItemType !== 'service' && item.itemType !== 'custom',
  );

  const unassignedServices = serviceLines.filter((item) => !item.performedByUserId);

  const openPicker = (item: typeof cart[number]) => {
    setPickerLineId(item.lineId);
    setPickerItemName(item.name);
    setPickerPerformedBy(item.performedByUserId ?? null);
    setPickerSoldBy(item.soldByUserId ?? null);
  };

  const handleApply = (
    lineId: string,
    performedByUserId: string | null,
    soldByUserId: string | null,
  ) => {
    onSetCartLineAttribution(lineId, performedByUserId, soldByUserId);
    setPickerLineId(null);
  };

  const renderRow = (item: typeof cart[number], isService: boolean) => {
    const assignedUserId = isService ? item.performedByUserId : item.soldByUserId;
    const isUnassignedService = isService && !assignedUserId;

    return (
      <TouchableOpacity
        key={item.lineId}
        style={[attrStyles.row, isUnassignedService && attrStyles.rowWarn]}
        onPress={() => openPicker(item)}
        accessibilityRole="button"
        accessibilityLabel={`Asignar responsable para ${item.name}`}
      >
        <View style={attrStyles.rowLeft}>
          <Text style={attrStyles.rowName} numberOfLines={1}>
            {item.name}
          </Text>
          <View style={attrStyles.rowMeta}>
            {isService ? (
              <Text style={attrStyles.rowTag}>Servicio</Text>
            ) : (
              <Text style={attrStyles.rowTag}>Producto</Text>
            )}
            {assignedUserId ? (
              <View style={attrStyles.avatarChip}>
                <Text style={attrStyles.avatarText}>{employeeInitials(assignedUserId)}</Text>
                <Text style={attrStyles.avatarName} numberOfLines={1}>
                  {employeeName(assignedUserId)}
                </Text>
              </View>
            ) : isUnassignedService ? (
              <View style={attrStyles.unassignedChip}>
                <Ionicons name="alert-circle" size={12} color={ticketColors.warn} />
                <Text style={attrStyles.unassignedText}>Sin asignar</Text>
              </View>
            ) : (
              <Text style={attrStyles.optionalText}>Opcional</Text>
            )}
          </View>
        </View>
        <Ionicons name="chevron-forward" size={14} color={ticketColors.ink3} />
      </TouchableOpacity>
    );
  };

  const attributedLines = cart.filter(
    (item) => item.performedByUserId && item.price > 0,
  );

  const handleSimulate = async () => {
    if (simulating || attributedLines.length === 0) return;
    const callId = ++simCallIdRef.current;
    setSimulating(true);
    setSimResult(null);
    try {
      const result = await simulateCommissionsBatch(
        attributedLines.map((line) => ({
          employeeUserId: line.performedByUserId!,
          amount: line.price * line.quantity,
          productId: line.productId ?? null,
          quantity: line.quantity,
          branchId: currentBranchId ?? null,
        })),
      );
      if (callId !== simCallIdRef.current) return;
      setSimResult({ total: result.total, count: result.count });
    } catch {
      if (callId !== simCallIdRef.current) return;
      showError('Error', 'No se pudo simular la comisión.');
    } finally {
      if (callId === simCallIdRef.current) setSimulating(false);
    }
  };

  const formatMoney = formatPosMoney;

  const picker = pickerLineId ? (
    <PosCartLineAttributionPicker
      visible={!!pickerLineId}
      lineId={pickerLineId}
      itemName={pickerItemName}
      currentPerformedByUserId={pickerPerformedBy}
      currentSoldByUserId={pickerSoldBy}
      onClose={() => setPickerLineId(null)}
      onApply={handleApply}
    />
  ) : null;

  return (
    <View style={styles.checkoutSectionBlock}>
      <TouchableOpacity
        style={attrStyles.collapseRow}
        onPress={() => setExpanded((v) => !v)}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        accessibilityLabel="Atribución de comisiones"
      >
        <Ionicons name="people-outline" size={15} color={ticketColors.ink3} />
        <Text style={attrStyles.collapseRowLabel}>Atribución de comisiones</Text>
        {unassignedServices.length > 0 ? (
          <Text style={attrStyles.hintWarn}>
            {unassignedServices.length} sin asignar
          </Text>
        ) : (
          <Text style={attrStyles.hint}>Opcional</Text>
        )}
        <Ionicons
          name={expanded ? 'chevron-up' : 'chevron-down'}
          size={14}
          color={ticketColors.ink3}
        />
      </TouchableOpacity>
      {!expanded ? null : attributedLines.length > 0 && unassignedServices.length === 0 ? (
        <View style={attrStyles.estimateRow}>
          {simResult ? (
            <Text style={attrStyles.estimateText}>
              Estimado: {formatMoney(simResult.total)} ({simResult.count} ítem{simResult.count !== 1 ? 's' : ''})
            </Text>
          ) : (
            <Text style={attrStyles.estimateHint}>
              {attributedLines.length} ítem{attributedLines.length !== 1 ? 's' : ''} con atribución
            </Text>
          )}
          <TouchableOpacity
            onPress={() => void handleSimulate()}
            disabled={simulating}
            style={attrStyles.simBtn}
            accessibilityRole="button"
            accessibilityLabel="Simular comisión"
          >
            {simulating ? (
              <ActivityIndicator size="small" color={ticketColors.gold} />
            ) : (
              <Text style={attrStyles.simBtnText}>Simular</Text>
            )}
          </TouchableOpacity>
        </View>
      ) : null}
      {expanded ? (
      <View style={attrStyles.body}>
        {serviceLines.length > 0 ? (
          <View style={attrStyles.sectionGroup}>
            <Text style={attrStyles.sectionLabel}>Servicios</Text>
            <ScrollView
              nestedScrollEnabled
              showsVerticalScrollIndicator={false}
              style={attrStyles.scrollArea}
            >
              {serviceLines.map((item) => renderRow(item, true))}
            </ScrollView>
          </View>
        ) : null}
        {productLines.length > 0 ? (
          <View style={attrStyles.sectionGroup}>
            <Text style={attrStyles.sectionLabel}>Productos</Text>
            <ScrollView
              nestedScrollEnabled
              showsVerticalScrollIndicator={false}
              style={attrStyles.scrollArea}
            >
              {productLines.map((item) => renderRow(item, false))}
            </ScrollView>
          </View>
        ) : null}
      </View>
      ) : null}
      {picker}
    </View>
  );
}

const attrStyles = StyleSheet.create({
  /** `.collapse-row` del proto — caja bordeada que invita a expandir. */
  collapseRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: ticketColors.paper,
  },
  collapseRowLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  hint: {
    flex: 1,
    fontSize: 12,
    color: ticketColors.ink3,
    textAlign: 'right',
    fontFamily: ticketFonts.sans,
  },
  hintWarn: {
    flex: 1,
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.warn,
    textAlign: 'right',
    fontFamily: ticketFonts.sans,
  },
  estimateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.rule,
  },
  estimateText: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.gold,
    fontFamily: ticketFonts.sans,
  },
  estimateHint: {
    fontSize: 11,
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  simBtn: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: ticketColors.gold,
  },
  simBtnText: {
    fontSize: 11,
    fontWeight: '600',
    color: ticketColors.gold,
    fontFamily: ticketFonts.sans,
  },
  body: { paddingHorizontal: spacing.sm, paddingBottom: spacing.sm },
  sectionGroup: { marginBottom: spacing.sm },
  sectionLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.ink3,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.xs,
    fontFamily: ticketFonts.sans,
  },
  scrollArea: { maxHeight: 240 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginBottom: 4,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  rowWarn: {
    borderColor: ticketColors.warn,
    backgroundColor: ticketColors.warnBg,
  },
  rowLeft: { flex: 1, gap: 4 },
  rowName: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  rowMeta: { flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  rowTag: {
    fontSize: 10,
    fontWeight: '600',
    color: ticketColors.ink3,
    textTransform: 'uppercase',
    fontFamily: ticketFonts.sans,
  },
  avatarChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: ticketColors.brandSoft,
    borderRadius: 999,
    paddingVertical: 2,
    paddingHorizontal: 8,
  },
  avatarText: {
    fontSize: 10,
    fontWeight: '700',
    color: ticketColors.brandInk,
    fontFamily: ticketFonts.sans,
  },
  avatarName: {
    fontSize: 11,
    fontWeight: '500',
    color: ticketColors.brandInk,
    fontFamily: ticketFonts.sans,
    maxWidth: 120,
  },
  unassignedChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  unassignedText: {
    fontSize: 11,
    fontWeight: '600',
    color: ticketColors.warn,
    fontFamily: ticketFonts.sans,
  },
  optionalText: {
    fontSize: 11,
    color: ticketColors.ink3,
    fontStyle: 'italic',
    fontFamily: ticketFonts.sans,
  },
});
